import { AxiosInstance } from "axios";
import { AO, AOPrestataire, Prestataire, AODashboard } from "../types/AO";

export const OffreService = {

  // AO
  getAllAO: async (api: AxiosInstance): Promise<AO[]> => {
    try {
      const response = await api.get("/ao/all");
      return response.data.map((item: any) => ({
        idAo: item.idAo,
        titre: item.titre,
        reference: item.reference,
        description: item.description,
        datePublication: item.datePublication,
        dateCloturation: item.dateCloturation,
        path: item.path,
        statut: item.statut,
        exigence: item.exigence || [],
        isPublished: item.isPublished || false,
      }));
    } catch (error) {
      console.error("Erreur lors de la récupération des AO :", error);
      throw error;
    }
  },

  getAOById: async (api: AxiosInstance, id: number): Promise<AO> => {
    try {
      const response = await api.get(`/ao/${id}`);

      const item = response.data;

      return {
        idAo: item.idAo,
        titre: item.titre,
        reference: item.reference,
        description: item.description,
        datePublication: item.datePublication,
        dateCloturation: item.dateCloturation,
        path: item.path,
        statut: item.statut,
        exigence: item.exigence || [],
        isPublished: item.isPublished || false,
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'AO avec id ${id} :`, error);
      throw error;
    }
  },

  getAllPublishedAO: async (api: AxiosInstance): Promise<AO[]> => {
    try {
        const response = await api.get("/ao/all-published");

        return response.data.map((item: any) => ({
        idAo: item.idAo,
        titre: item.titre,
        reference: item.reference,
        description: item.description,
        datePublication: item.datePublication,
        dateCloturation: item.dateCloturation,
        path: item.path,
        statut: item.statut,
        exigence: item.exigence || [],
        isPublished: item.published ?? item.isPublished ?? false,
        }));
    } catch (error) {
        console.error("Erreur lors de la récupération des AO publiés :", error);
        throw error;
    }
  },

  createAO: async (api: AxiosInstance, ao: AO): Promise<AO> => {
    try {
      const formData = new FormData();

      formData.append("titre", ao.titre);
      formData.append("reference", ao.reference);
      formData.append("description", ao.description);            
      formData.append("datePublication", ao.datePublication || "");
      formData.append("dateCloturation", ao.dateCloturation || "");
      formData.append("statut", ao.statut);
      formData.append("exigence", JSON.stringify(ao.exigence || []));

      if (ao.daoFile) {
        formData.append("daoFile", ao.daoFile);
      }

      const response = await api.post("/ao/create", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      return {
        idAo: response.data.idAo,
        titre: response.data.titre,
        reference: response.data.reference,
        description: response.data.description,
        datePublication: response.data.datePublication,
        dateCloturation: response.data.dateCloturation,
        path: response.data.path,
        statut: response.data.statut,
        exigence: response.data.exigence || [],
      };

    } catch (error) {
      console.error("Erreur lors de la création de l'AO :", error);
      throw error;
    }
  },

  // "cloture", "evaluation", "resultat_de_marche", "rejeter", "valider"
  changeAOStatus: async (api: AxiosInstance, idAo: number, status: string): Promise<AO> => {
    try {
      const response = await api.put(`/ao/status/${idAo}`, null, {
        params: { status }
      });

      return {
        idAo: response.data.idAo,
        titre: response.data.titre,
        reference: response.data.reference,
        description: response.data.description,
        datePublication: response.data.datePublication,
        dateCloturation: response.data.dateCloturation,
        path: response.data.path,
        statut: response.data.statut,
        exigence: response.data.exigence || [],
      };
    } catch (error) {
      console.error(`Erreur lors de la modification du statut de l'AO ${idAo}:`, error);
      throw error;
    }
  },

  // AO Prestataire
  createAOPrestataires: async (api: AxiosInstance, list: AOPrestataire[]): Promise<AOPrestataire[]> => {
    try {
      const response = await api.post("/ao-prestataire/creates", list);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'insertion des AO Prestataire :", error);
      throw error;
    }
  },

  getByIdAO: async (api: AxiosInstance, idAo: number): Promise<AOPrestataire[]> => {
    try {
      const response = await api.get(`/ao-prestataire/by-ao/${idAo}`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des prestataires par AO :", error);
      throw error;
    }
  },

  enableStatus: async (api: AxiosInstance, idAo: number): Promise<string> => {
    try {
      const response = await api.put(`/ao-prestataire/enable-status/${idAo}`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'activation du status :", error);
      throw error;
    }
  },

  // Prestataire
  createPrestataire: async (
    api: AxiosInstance,
    prestataire: Prestataire
  ): Promise<Prestataire> => {
    try {
      const response = await api.post("/prestataire/create", prestataire);
      return response.data;

    } catch (error: any) {
      console.error("Erreur lors de la création du prestataire :", error);

      if (error.response?.status === 400) {
        const code = error.response.data;

        if (code === "email.taken") {
          throw new Error("EMAIL_TAKEN");
        }

        throw new Error("BAD_REQUEST");
      }

      if (error.response?.status === 500) {
        throw new Error("SERVER_ERROR");
      }

      throw new Error("UNKNOWN_ERROR");
    }
  },

  getAllPrestataires: async (api: AxiosInstance): Promise<Prestataire[]> => {
    try {
      const response = await api.get("/prestataire/all");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des prestataires :", error);
      throw error;
    }
  },

  publishAO: async (api: AxiosInstance, idAo: number): Promise<AO> => {
    const response = await api.put(`/ao/publish/${idAo}`);
    return response.data;
  },

  // Dashboard
  getDashboard: async (api: AxiosInstance, annee: number): Promise<AODashboard[]> => {
    const response = await api.get(`/ao-dashboard/${annee}`);
    return response.data;
  },

  getDashboardSummary: async (api: AxiosInstance, annee: number): Promise<AODashboard> => {
    const response = await api.get(`/ao-dashboard/summary/${annee}`);
    return response.data;
  },
};